import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { FieldWrapper } from '@ngx-formly/core';
import { DataFormEvent, DataFormModel, FileUploaderFieldModel } from '@shared/components/data-form/data-form.model';
import { DataFormService } from '@shared/components/data-form/data-form.service';
import { PageLayoutService } from '@shared/components/page-layout/page-layout.service';
import { ParentDrawerUploadResponse } from '@shared/components/page-layout/page-layout.types';
import { EventMediaUpload, MediaUpload } from '@shared/db/db-domain.model';
import { MediaUploadStatus } from '@shared/db/db.enum';
import { EventMediaUploadStorageService } from '@shared/db/services/event-media-upload-storage.service';
import { MediaUploadStorageService } from '@shared/db/services/media-upload-storage.service';
import { ReceivedStorageItemInstancesService } from '@shared/db/services/received-storage-item-instances.service';
import { MediaTypeId } from '@shared/enums/media-type.enum';
import { FileExtensionColorPipe } from '@shared/pipes/file-extension-color.pipe';
import { FileExtensionPipe } from '@shared/pipes/file-extension.pipe';
import { AlertService } from '@shared/services/alert.service';
import { MediaService } from '@shared/services/media.service';
import { FileSystemFileEntry, NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { firstValueFrom } from 'rxjs';

type UploadFileId = string | number;
type ReadFileEntryCallback = (callback: (file: File) => void, errorCallback?: (error: Error) => void) => void;

interface UploadPreviewFile extends File {
    id?: UploadFileId;
    imageUrl?: string | ArrayBuffer | null;
    isLoading?: boolean;
}

@Component({
    selector: 'app-file-uploader',
    templateUrl: './file-uploader.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatLabel,
        NgxFileDropModule,
        MatIcon,
        NgClass,
        FileExtensionPipe,
        FileExtensionColorPipe,
    ],
})
export class FileUploaderComponent extends FieldWrapper implements OnInit {
    private alertService = inject(AlertService);
    private mediaService = inject(MediaService);
    private mediaUploadStorageService = inject(MediaUploadStorageService);
    private eventMediaUploadService = inject(EventMediaUploadStorageService);
    private receivedStorageItemInstancesService = inject(ReceivedStorageItemInstancesService);
    private layoutService = inject(PageLayoutService);
    private formService = inject(DataFormService);
    private destroyRef = inject(DestroyRef);

    borderStyle = input(false);
    filesUploadedEvent = output<UploadPreviewFile[]>();
    
    files = signal<UploadPreviewFile[]>([]);
    accept = signal('*');
    private fileIds = signal<UploadFileId[]>([]);
    
    readonly hasFiles = computed(() => this.files().length > 0);

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        this.field ??= {} as FieldWrapper;
        this.field.props ??= {};
        this.accept.set((this.field.props.accept as string) || '*');

        this.formControl?.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                if (!value) {
                    this.clearSelectedFiles();
                }
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    async onSelect(entries: NgxFileDropEntry[]): Promise<void> {
        const selectedEntries = this.isMultiple() ? entries : entries.slice(0, 1);
        if (!selectedEntries.length) return;
        if (!this.isMultiple()) this.clearSelectedFiles();

        const previewResults = await Promise.allSettled(selectedEntries.map((entry) => this.createPreviewFile(entry)));

        const previewFiles = previewResults
            .filter((result): result is PromiseFulfilledResult<UploadPreviewFile | null> => result.status === 'fulfilled')
            .map((result) => result.value)
            .filter((file): file is UploadPreviewFile => file !== null);

        const failedFileNames = previewResults
            .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
            .map((result) => this.getReadErrorFileName(result.reason as { fileName: string }));

        if (failedFileNames.length) {
            this.alertService.errorToast('Error!', `Failed to read: ${failedFileNames.join(', ')}`);
        }

        if (!previewFiles.length) return;

        this.files.update((currentFiles) => [...currentFiles, ...previewFiles]);
        this.filesUploadedEvent.emit(this.files());

        if (this.shouldSkipUpload()) {
            this.layoutService.setParentDrawerSubject(this.files());
            this.layoutService.closeParentDrawer();
            return;
        }

        const backgroundMessage = this.isBackgroundUpload() ? ' in the background' : '';
        const message = previewFiles.length > 1
            ? `Your files are uploading${backgroundMessage}!`
            : `Your file is uploading${backgroundMessage}!`;

        this.alertService.warnToast('Please wait', message, 1000);

        if (this.isBackgroundUpload()) {
            await this.uploadMediaToStorageAsync(previewFiles);
        }
        else if (this.field?.props?.apiUrl) {
            await this.uploadMediaToApi(previewFiles);
        }
        else {
            await this.uploadMedia(previewFiles);
        }
    }

    onRemove(file: UploadPreviewFile): void {
        this.files.update((currentFiles) => currentFiles.filter((currentFile) => currentFile !== file));

        const nextFileIds = this.fileIds().filter((fileId) => fileId !== file.id);
        this.fileIds.set(nextFileIds);
        this.updateFormControlValue(nextFileIds);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private async createPreviewFile(entry: NgxFileDropEntry): Promise<UploadPreviewFile | null> {
        if (!entry.fileEntry.isFile) return null;

        const fileEntry = entry.fileEntry as FileSystemFileEntry;

        try {
            const file = await this.readFileEntry(fileEntry);
            return await this.readPreviewFile(file);
        } catch {
            const error: { fileName: string } = {
                fileName: entry.relativePath || fileEntry.name || 'File',
            };
            throw error;
        }
    }

    private readFileEntry(fileEntry: FileSystemFileEntry): Promise<File> {
        return new Promise<File>((resolve, reject) => {
            try {
                const readFile: ReadFileEntryCallback = fileEntry.file.bind(fileEntry) as ReadFileEntryCallback;
                readFile(resolve, reject);
            } catch (error) {
                reject(error instanceof Error ? error : new Error('Failed to read file entry.', { cause: error }));
            }
        });
    }

    private readPreviewFile(file: File): Promise<UploadPreviewFile> {
        return new Promise<UploadPreviewFile>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const previewFile = file as UploadPreviewFile;
                previewFile.imageUrl = event.target?.result;
                previewFile.isLoading = !this.shouldSkipUpload();
                resolve(previewFile);
            };
            reader.onerror = () => reject(new Error('Failed to read file.', { cause: reader.error }));
            reader.readAsDataURL(file);
        });
    }

    private async uploadMediaToStorageAsync(files: UploadPreviewFile[]): Promise<void> {
        if (!files.length) {
            this.alertService.errorToast('Error!', 'No files to upload.');
            return;
        }

        this.setFilesLoading(files, false);

        const uploads: MediaUpload[] = await Promise.all(files.map(async (file) => ({
            fileData: await file.arrayBuffer(),
            fileType: file.type,
            fileName: file.name,
            size: file.size,
            status: MediaUploadStatus.Pending,
            mediaTypeId: this.getMediaType(),
        } as MediaUpload)));

        const ids = await this.mediaUploadStorageService.addMany(uploads);

        if (!ids?.length) {
            this.clearSelectedFiles();
            this.alertService.errorToast('Error!', 'Failed to add files for upload. Please try again.');
            return;
        }

        const eventMedias: EventMediaUpload[] = ids.map((mediaId) => ({
            mediaUploadId: mediaId,
            subscriptionEventId: (this.field?.model as FileUploaderFieldModel)?.subscriptionEventId,
            storageItemInstanceId: (this.field?.model as FileUploaderFieldModel)?.storageItemInstanceId,
            subscriptionId: (this.field?.model as FileUploaderFieldModel)?.subscriptionId,
            projectId: (this.field?.model as FileUploaderFieldModel)?.projectId,
            retries: 0,
            status: MediaUploadStatus.Pending,
            error: '',
            mediaTypeId: this.getMediaType(),
        } as EventMediaUpload));

        await this.eventMediaUploadService.addMany(eventMedias);

        if (
            (this.field?.model as FileUploaderFieldModel)?.aiAssisted
            && (this.field?.model as FileUploaderFieldModel)?.instance
            && (this.field?.model as FileUploaderFieldModel)?.subscriptionEventId
        ) {
            try {
                await this.receivedStorageItemInstancesService.addForAnalysis(
                    (this.field.model as FileUploaderFieldModel).subscriptionEventId,
                    (this.field.model as FileUploaderFieldModel).instance
                );
            } catch {
                this.alertService.errorToast('Error!', 'Failed to add item for AI analysis tracking.');
            }
        }

        await this.eventMediaUploadService.checkPendingUploadsAsync();

        this.fileIds.set(ids);
        this.updateFormControlValue(ids);

        if (this.field?.props?.closeOnUpload) {
            const event: DataFormEvent = {
                keepOpen: false,
                currentData: this.field?.model as DataFormModel,
                dirty: true,
                model: this.field?.model as DataFormModel,
                formUrl: MediaTypeId[this.getMediaType()],
            };

            this.layoutService.setParentDrawerSubject(event);
        }
    }

    private async uploadMedia(files: UploadPreviewFile[]): Promise<void> {
        this.formService.isUploading.set(true);

        try {
            const uploadResults = await Promise.allSettled(
                files.map(async (file) => {
                    try {
                        const formData = new FormData();
                        formData.append('file', file);

                        const mediaType = this.getMediaType();
                        if (mediaType) formData.append('mediaType', mediaType.toString());

                        this.formControl?.setValidators([Validators.required]);

                        const response = await firstValueFrom(this.mediaService.upload(formData));
                        return { file, response };
                    } catch {
                        const error: { file: UploadPreviewFile } = { file };
                        throw error;
                    }
                })
            );

            uploadResults.forEach((result) => {
                if (result.status === 'fulfilled') {
                    this.applyUploadedMediaResponse(result.value.file, result.value.response);
                    this.alertService.successToast('Success', "Your file has been uploaded successfully.");
                    return;
                }

                this.handleUploadFailure(result.reason as { file: UploadPreviewFile });
            });
        } finally {
            this.formService.isUploading.set(false);
        }
    }

    private async uploadMediaToApi(files: UploadPreviewFile[]): Promise<void> {
        this.formService.isUploading.set(true);

        try {
            const uploadResults = await Promise.allSettled(
                files.map(async (file) => {
                    try {
                        const formData = new FormData();
                        formData.append('file', file);

                        if (this.field?.props?.additionalData) {
                            for (const key in this.field.props.additionalData) {
                                if (Object.prototype.hasOwnProperty.call(this.field.props.additionalData, key)) {
                                    const element = (this.field.props.additionalData as DataFormModel)[key];
                                    formData.append(key, element?.toString());
                                }
                            }
                        }

                        this.formControl?.setValidators([Validators.required]);

                        const response = await firstValueFrom(
                            this.mediaService.uploadToApi(formData, this.field?.props?.apiUrl as string)
                        );

                        return { file, response } as { file: UploadPreviewFile; response: boolean };
                    } catch {
                        const error: { file: UploadPreviewFile } = { file };
                        throw error;
                    }
                })
            );

            uploadResults.forEach((result) => {
                if (result.status === 'fulfilled') {
                    this.applyApiUploadResponse(result.value.file, result.value.response);
                    this.alertService.successToast('Success', "Your file has been uploaded successfully.");
                    return;
                }

                this.handleUploadFailure(result.reason as { file: UploadPreviewFile });
            });
        } finally {
            this.formService.isUploading.set(false);
        }
    }

    private applyUploadedMediaResponse(file: UploadPreviewFile, response: ParentDrawerUploadResponse): void {
        const mediaIds = response.medias
            .map((media) => media?.id)
            .filter((mediaId): mediaId is number => typeof mediaId === 'number');

        const [firstMediaId] = mediaIds;
        if (typeof firstMediaId === 'number') {
            file.id = firstMediaId;
        }
        file.isLoading = false;
        this.files.update((currentFiles) => [...currentFiles]);

        const nextFileIds = [...this.fileIds(), ...mediaIds];
        this.fileIds.set(nextFileIds);
        this.updateFormControlValue(nextFileIds);
    }

    private setFilesLoading(files: UploadPreviewFile[], isLoading: boolean): void {
        files.forEach((file) => {
            file.isLoading = isLoading;
        });

        this.files.update((currentFiles) => [...currentFiles]);
    }

    private applyApiUploadResponse(file: UploadPreviewFile, response: boolean): void {
        file.isLoading = false;
        this.files.update((currentFiles) => [...currentFiles]);

        this.formControl?.setValue(response);
    }

    private updateFormControlValue(fileIds: UploadFileId[]): void {
        if (!this.formControl) return;

        const value = this.isMultiple() ? fileIds : (fileIds[0] ?? null);
        this.formControl.setValue(value);
    }

    private clearSelectedFiles(): void {
        this.files.set([]);
        this.fileIds.set([]);
    }

    private handleUploadFailure(reason: { file: UploadPreviewFile }): void {
        this.files.update((currentFiles) => currentFiles.filter((currentFile) => currentFile !== reason.file));
        this.alertService.errorToast('Error!', `Failed to upload ${reason.file.name}. Please try again.`);
        this.formControl?.clearValidators();
    }

    private getReadErrorFileName(reason: { fileName: string }): string {
        return reason.fileName;
    }

    private isMultiple(): boolean {
        return this.field?.props?.multiple as boolean;
    }

    private shouldSkipUpload(): boolean {
        return (this.field?.model as FileUploaderFieldModel)?.skipUploadFile;
    }

    private isBackgroundUpload(): boolean {
        return this.field?.props?.backgroundUpload as boolean;
    }

    private getMediaType(): number | null {
        return (this.field?.props?.mediaType as number) ?? null;
    }
}
